/**
 * Stories have been implemented
 */
/* eslint-disable local-rules/require-storybook-documentation */
import { FC } from 'react';
import dynamic from 'next/dynamic';
const LoadingUpsell = dynamic(() =>
    import(
        /* webpackChunkName: "LoadingUpsell" */
        './experiences/LoadingUpsell'
    ).then(mod => mod.LoadingUpsell)
);

const NewApplicationUpsell = dynamic(() =>
    import(
        /* webpackChunkName: "NewApplicationUpsell" */
        './experiences/NewApplicationUpsell'
    ).then(mod => mod.NewApplicationUpsell)
);
const OutstandingTasksUpsell = dynamic(() =>
    import(
        /* webpackChunkName: "OutstandingTasksUpsell" */
        './experiences/OutstandingTasksUpsell'
    ).then(mod => mod.OutstandingTasksUpsell)
);
const VerifiedPreApprovalUpsell = dynamic(() =>
    import(
        /* webpackChunkName: "VerifiedPreApprovalUpsell" */
        './experiences/VerifiedPreApprovalUpsell'
    ).then(mod => mod.VerifiedPreApprovalUpsell)
);
const WaitingToCloseUpsell = dynamic(() =>
    import(
        /* webpackChunkName: "WaitingToCloseUpsell" */
        './experiences/WaitingToCloseUpsell'
    ).then(mod => mod.WaitingToCloseUpsell)
);

const OutstandingTasksFirstPartyUpsell = dynamic(() =>
    import(
        /* webpackChunkName: "OutstandingTasksFirstPartyUpsell" */
        './experiences/OutstandingTasksFirstPartyUpsell'
    ).then(mod => mod.OutstandingTasksFirstPartyUpsell)
);

const VerifiedPreApprovalFirstPartyUpsell = dynamic(() =>
    import(
        /* webpackChunkName: "VerifiedPreApprovalFirstPartyUpsell" */
        './experiences/VerifiedPreApprovalFirstPartyUpsell'
    ).then(mod => mod.VerifiedPreApprovalFirstPartyUpsell)
);
import { UpsellState } from './types';
import { UpsellCTAVariant } from './anticorruption/UpsellCTAVariant';

/**
 * This module provides an Upsell micro-frontend that varies depending on which personalized
 * next step the user is in. While it requires a large set of data, only a subset of the props
 * passed in are required for each individual experience, which is dynamically loaded to reduce
 * the initial payload size
 */
export const Upsell: FC<UpsellState> = ({
    isLoading,
    variant,
    trackPrimaryCTAClick,
    trackSecondaryCTAClick,
    valuation,
    address,
    documentURL,
    downloadURL,
    onPrimaryCTAClick,
    onSecondaryCTAClick,
    loanID,
    purchasePrice,
}) => {
    if (isLoading) {
        return <LoadingUpsell />;
    }
    if (!variant) {
        return null;
    }
    switch (variant) {
        case UpsellCTAVariant.FIRST_PARTY_NEW_APPLICATION: {
            return (
                <NewApplicationUpsell
                    onPrimaryCTAClick={onPrimaryCTAClick}
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                    trackSecondaryCTAClick={trackSecondaryCTAClick}
                    valuation={valuation}
                    loanID={loanID}
                />
            );
        }
        case UpsellCTAVariant.NEW_APPLICATION: {
            return (
                <NewApplicationUpsell
                    onPrimaryCTAClick={onPrimaryCTAClick}
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                    trackSecondaryCTAClick={trackSecondaryCTAClick}
                    valuation={valuation}
                    loanID={loanID}
                />
            );
        }
        case UpsellCTAVariant.FIRST_PARTY_OUTSTANDING_TASKS:
        case UpsellCTAVariant.OUTSTANDING_TASKS: {
            return (
                <OutstandingTasksUpsell
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                    address={address}
                    onPrimaryCTAClick={onPrimaryCTAClick}
                />
            );
        }
        case UpsellCTAVariant.VERIFIED_PREAPPROVAL: {
            return (
                <VerifiedPreApprovalUpsell
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                    purchasePrice={purchasePrice}
                    documentURL={documentURL}
                    downloadURL={downloadURL}
                />
            );
        }
        case UpsellCTAVariant.FIRST_PARTY_WAITING_TO_CLOSE:
        case UpsellCTAVariant.WAITING_TO_CLOSE: {
            return (
                <WaitingToCloseUpsell
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                    address={address}
                    onPrimaryCTAClick={onPrimaryCTAClick}
                />
            );
        }
        case UpsellCTAVariant.FIRST_PARTY_VERIFIED_PREAPPROVAL_NO_TRID_OUTSTANDING_TASKS: {
            return (
                <OutstandingTasksFirstPartyUpsell
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                    onPrimaryCTAClick={onPrimaryCTAClick}
                    onSecondaryCTAClick={onSecondaryCTAClick}
                    trackSecondaryCTAClick={trackSecondaryCTAClick}
                />
            );
        }
        case UpsellCTAVariant.FIRST_PARTY_VERIFIED_PREAPPROVAL: {
            return (
                <VerifiedPreApprovalFirstPartyUpsell
                    trackPrimaryCTAClick={trackPrimaryCTAClick}
                    purchasePrice={purchasePrice}
                    onPrimaryCTAClick={onPrimaryCTAClick}
                />
            );
        }
    }
};
